import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, User, Camera, Users, ArrowLeft, Sparkles, PartyPopper } from "lucide-react";
import { currentUser } from "@/lib/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASKS = [
  { id: "complete_profile", icon: User,   title: "Completa tu perfil",                      reward: 50,  cta: "Completar", action: "post_created", amount: 50 },
  { id: "first_upload",     icon: Camera, title: "Sube tu primera foto o canción",         reward: 100, cta: "Subir",     action: "music_upload", amount: 100 },
  { id: "first_community",  icon: Users,  title: "Únete a tu primera Comunidad Poderosa",  reward: 50,  cta: "Unirme",    action: "challenge_easy", amount: 50 },
];

export default function MissionStep({ onFinish, onPrev }) {
  const [done, setDone] = useState({});
  const [busy, setBusy] = useState(null);

  const completed = TASKS.filter((t) => done[t.id]).length;
  const earned = TASKS.filter((t) => done[t.id]).reduce((a, t) => a + t.reward, 0);
  const allDone = completed === TASKS.length;

  const complete = async (task) => {
    if (done[task.id] || busy) return;
    setBusy(task.id);
    try {
      await axios.post(`${API}/sparks/earn`, {
        user_id: currentUser.id,
        action: task.action,
        amount: task.amount,
        ref: task.title,
      });
      setDone((d) => ({ ...d, [task.id]: true }));
      toast.success(`+${task.reward} Sparks · ${task.title}`);
    } catch {
      toast.error("No se pudo registrar la tarea");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="mission-step" className="grid grid-cols-12 gap-8 items-start">
      {/* Header */}
      <div className="col-span-12 md:col-span-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Misión 01
        </p>
        <h2 className="mt-3 font-serif text-5xl md:text-6xl tracking-tighter leading-[0.95]">
          Primeros <span className="italic text-coral">Sparks</span>.
        </h2>
        <p className="mt-4 text-base md:text-lg text-ink/80 leading-relaxed text-balance">
          Tres tareas rápidas. Activan tu perfil y desbloquean tus primeros 200 ✦. Las puedes hacer ahora — o cuando quieras desde tu Perfil.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Stat k={`${completed} / 3`} v="completadas" />
          <Stat k={`+${earned}`} v="Sparks ganados" highlight />
        </div>

        <div className="mt-7 flex items-center gap-3">
          <button
            onClick={onPrev}
            data-testid="mission-prev"
            className="inline-flex items-center gap-2 border hairline rounded-full px-4 py-2.5 text-sm hover:border-ink transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Atrás
          </button>
          <button
            onClick={onFinish}
            data-testid="mission-finish"
            className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm transition ${
              allDone ? "bg-coral text-cream hover:bg-coral-hover" : "bg-ink text-cream hover:bg-coral"
            }`}
          >
            {allDone ? <><PartyPopper className="w-4 h-4" /> Entrar a Ovamx</> : "Saltar e ir a Ovamx"}
          </button>
        </div>
      </div>

      {/* Tasks list */}
      <div className="col-span-12 md:col-span-7">
        <div className="space-y-3">
          {TASKS.map((t) => {
            const Icon = t.icon;
            const isDone = !!done[t.id];
            const loading = busy === t.id;
            return (
              <div
                key={t.id}
                data-testid={`task-${t.id}`}
                className={`border rounded-2xl p-5 flex items-center gap-4 transition ${
                  isDone ? "bg-cream-deep border-coral/30" : "bg-cream-card hairline hover:border-coral hover:shadow-lg"
                }`}
              >
                <span className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isDone ? "bg-coral text-cream" : "bg-coral/10 text-coral"}`}>
                  {isDone ? <Check className="w-5 h-5" strokeWidth={2.4} /> : <Icon className="w-5 h-5" strokeWidth={1.8} />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isDone ? "line-through text-ink-mid" : "text-ink"}`}>{t.title}</p>
                  <p className="text-xs font-mono uppercase tracking-wider text-ink-mid mt-0.5">
                    +{t.reward} Sparks ✦
                  </p>
                </div>
                <button
                  onClick={() => complete(t)}
                  disabled={isDone || loading}
                  data-testid={`task-${t.id}-cta`}
                  className={`text-xs px-4 py-2 rounded-full transition ${
                    isDone
                      ? "bg-coral text-cream cursor-default"
                      : "bg-ink text-cream hover:bg-coral disabled:opacity-50"
                  }`}
                >
                  {isDone ? "Hecho ✦" : (loading ? "..." : t.cta)}
                </button>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="mt-5 px-1">
          <div className="flex items-center justify-between mb-2 text-xs font-mono uppercase tracking-[0.25em] text-ink-mid">
            <span>progreso de la misión</span>
            <span data-testid="mission-progress">{Math.round((completed / TASKS.length) * 100)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-cream-deep overflow-hidden">
            <div
              data-testid="mission-progress-bar"
              className="h-full bg-gradient-to-r from-coral to-coral-hover transition-all duration-700 ease-out"
              style={{ width: `${(completed / TASKS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v, highlight }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-ink text-cream" : "bg-cream-deep"}`}>
      <div className={`font-serif text-3xl tracking-tighter ${highlight ? "text-coral" : "text-ink"}`}>{k}</div>
      <div className={`font-mono text-[10px] uppercase tracking-[0.25em] mt-0.5 ${highlight ? "text-cream/65" : "text-ink-mid"}`}>{v}</div>
    </div>
  );
}
